<script setup>
// Config do node "Prospecção": nicho, local, quantos por dia (o sistema divide e
// manda aos poucos), fonte do decisor, mensagem-base e janela de horário.
// Sincroniza no backend -> ProspectingCampaign (Prospecting::CampaignSyncService).
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import prospectingAPI from 'dashboard/api/agents/prospecting';
import flexcallsAPI from 'dashboard/api/agents/flexcalls';
import ProspectingListsAPI from 'dashboard/api/prospectingLists';
import MarketingEmailTemplatesAPI from 'dashboard/api/marketingEmailTemplates';
import CrmSourceForm from 'dashboard/routes/dashboard/lead-prospector/CrmSourceForm.vue';

const props = defineProps({ data: { type: Object, required: true } });
const emit = defineEmits(['update', 'remove', 'close']);
const { t } = useI18n();
const store = useStore();
const route = useRoute();

// Providers Meta oficiais (WhatsApp Cloud API) — outbound frio anti-ban via HSM.
const OFFICIAL_PROVIDERS = ['whatsapp_cloud', 'default'];
const funnels = computed(() => store.getters['funnels/getFunnels'] || []);

// Etapas do funil escolhido, na ordem configurada. Mesmo formato usado pelo
// canvas de campanhas (funnel.stages é um objeto nome -> { label, position }).
const stagesOf = funnelId => {
  const funnel = (funnels.value || []).find(
    f => Number(f.id) === Number(funnelId)
  );
  if (!funnel?.stages) return [];
  return Object.entries(funnel.stages)
    .sort((a, b) => (a[1]?.position || 0) - (b[1]?.position || 0))
    .map(([name, cfg]) => ({ name, label: cfg?.label || name }));
};

// Trocar o funil zera a etapa: etapa de outro funil é pior que etapa vazia.
const onFunnelChange = e => {
  emit('update', {
    funnelId: Number(e.target.value) || null,
    stageName: '',
  });
};

const generating = ref('');
// Tom da mensagem gerada por IA: whatsapp (padrão) / instagram / facebook.
const copyNetwork = ref('whatsapp');
const COPY_NETWORKS = ['whatsapp', 'instagram', 'facebook'];
const COPY_NETWORK_ICONS = {
  whatsapp: 'i-lucide-message-circle',
  instagram: 'i-lucide-instagram',
  facebook: 'i-lucide-facebook',
};
const up = (key, val) => emit('update', { [key]: val });
const upNum = (key, val) => emit('update', { [key]: Number(val) || 0 });

// Prospecções salvas (Hub de Prospecção) disponíveis pra reusar no modo "list".
const savedLists = ref([]);
async function loadSavedLists() {
  try {
    const { data } = await ProspectingListsAPI.get();
    savedLists.value = Array.isArray(data) ? data : data?.payload || [];
  } catch {
    savedLists.value = [];
  }
}

// Fila de prospecções. Aceita o formato antigo (uma lista só) para não perder
// a escolha de nós salvos antes desta tela existir.
const queuedListIds = computed(() => {
  const ids = props.data.prospectingListIds;
  if (Array.isArray(ids) && ids.length) return ids.map(Number);
  return props.data.prospectingListId
    ? [Number(props.data.prospectingListId)]
    : [];
});

const isQueued = id => queuedListIds.value.includes(Number(id));

// A lista nasce fechada quando já há escolha — é aí que ela ocupa espaço demais.
// Sem nenhuma escolhida ela nasce aberta: é o primeiro passo obrigatório do nó
// (a campanha nem liga sem prospecção), e escondê-lo custaria um clique cego.
const listOpen = ref(queuedListIds.value.length === 0);

const queuedTotal = computed(() =>
  savedLists.value
    .filter(l => isQueued(l.id))
    .reduce((sum, l) => sum + (l.total_count || l.estimated_count || 0), 0)
);

// Posição na fila, 1-based; vazio quando não está marcada.
const queuePosition = id => {
  const idx = queuedListIds.value.indexOf(Number(id));
  return idx === -1 ? '' : String(idx + 1);
};

const toggleList = (id, checked) => {
  const current = queuedListIds.value;
  const next = checked
    ? [...current, Number(id)]
    : current.filter(x => x !== Number(id));
  const payload = { prospectingListIds: next };
  // Esvaziar a fila tem de apagar a chave antiga junto: o backend trata
  // prospectingListIds: [] igual a campo ausente e cairia de volta no
  // prospectingListId singular, ressuscitando uma lista que o usuário
  // acabou de remover.
  if (!next.length) payload.prospectingListId = null;
  emit('update', payload);
};

// Prospecção Do CRM criada aqui mesmo (etiqueta ou etapa do funil): entra no
// fim da fila já marcada, sem precisar ir ao hub.
const crmFormOpen = ref(false);
const onCrmListCreated = async list => {
  crmFormOpen.value = false;
  await loadSavedLists();
  if (!isQueued(list.id)) toggleList(list.id, true);
  listOpen.value = true;
};

// Números WhatsApp disponíveis (oficial Meta primeiro).
const whatsappInboxes = computed(() => {
  const all = store.getters['inboxes/getInboxes'] || [];
  return all
    .filter(i => i.channel_type === 'Channel::Whatsapp')
    .map(i => ({
      id: i.id,
      name: i.name,
      official: OFFICIAL_PROVIDERS.includes(
        i.provider || i.additional_attributes?.provider || ''
      ),
    }))
    .sort((a, b) => Number(b.official) - Number(a.official));
});

const selectedInbox = computed(() =>
  whatsappInboxes.value.find(i => i.id === Number(props.data.whatsappInboxId))
);
const isOfficialMeta = computed(() => selectedInbox.value?.official === true);

// Templates aprovados do número escolhido (só APPROVED no outbound frio).
const approvedTemplates = computed(() => {
  if (!props.data.whatsappInboxId) return [];
  const tpls =
    store.getters['inboxes/getFilteredWhatsAppTemplates'](
      Number(props.data.whatsappInboxId)
    ) || [];
  return tpls.filter(tpl => (tpl.status || '').toUpperCase() === 'APPROVED');
});

// Um template salvo que NÃO está entre os aprovados do número escolhido (ex.: o
// número foi trocado e o template ficou apontando pro antigo). Sem isso o <select>
// renderiza em branco e o valor "some" sem aviso.
const isUnknownTemplate = name =>
  !!name && !approvedTemplates.value.some(tpl => tpl.name === name);

// Estilo padrão dos <select> do nó. A seta global (_base.scss) é posicionada via
// bg-origin-content + right -1rem, que quebra (sai do box) quando o padding é
// menor que isso. Aqui reposicionamos a seta pela BORDA (border-box, 0.75rem da
// direita) e damos pr-9 de espaço — assim a setinha fica certinha dentro do box.
const selectClass =
  'w-full rounded-lg border border-n-weak bg-n-solid-2 py-2 pl-3 pr-9 text-sm text-n-slate-12 [background-origin:border-box] bg-[right_0.75rem_center]';

const onInboxChange = e => {
  // Trocar de número zera o template (templates são por número).
  emit('update', {
    whatsappInboxId: Number(e.target.value) || null,
    templateId: null,
  });
};

// Canais do 1º contato, em ORDEM de tentativa. O motor dá um toque por lead:
// o primeiro canal da ordem que está ligado, alcança o registro e ainda cabe
// no limite diário (Prospecting::ChannelResolver). Cada mensagem enviada
// consome crédito conforme o canal (custo mostrado na linha).
const CHANNEL_KEYS = ['whatsapp', 'sms', 'email', 'call', 'instagram'];
const CHANNEL_ICONS = {
  whatsapp: 'i-lucide-message-circle',
  sms: 'i-lucide-message-square-text',
  email: 'i-lucide-mail',
  call: 'i-lucide-phone',
  instagram: 'i-lucide-instagram',
};
// Dado da prospecção que cada canal exige (regra do Ivan: canal só liga se a
// prospecção tem aquele dado). SMS e ligação servem com qualquer telefone.
const CHANNEL_DATA = {
  whatsapp: 'whatsapp',
  sms: 'phone',
  email: 'email',
  call: 'phone',
  instagram: 'instagram',
};

// Formato novo (array ordenado) ou, em nó salvo antes desta tela, o canal
// único de antes vira o único ligado.
const normalizeChannels = data => {
  const raw = Array.isArray(data.channels) ? data.channels : [];
  const known = raw.filter(c => CHANNEL_KEYS.includes(c?.key));
  if (known.length) {
    const seen = [];
    known.forEach(c => {
      if (seen.some(x => x.key === c.key)) return;
      seen.push({ key: c.key, enabled: c.enabled === true });
    });
    CHANNEL_KEYS.forEach(key => {
      if (!seen.some(x => x.key === key)) seen.push({ key, enabled: false });
    });
    return seen;
  }
  const legacy = CHANNEL_KEYS.includes(data.channel)
    ? data.channel
    : 'whatsapp';
  return CHANNEL_KEYS.map(key => ({ key, enabled: key === legacy }));
};

const channels = computed(() => normalizeChannels(props.data));
const enabledChannels = computed(() => channels.value.filter(c => c.enabled));
const isOn = key => enabledChannels.value.some(c => c.key === key);
// Compat com o que o backend e o resto do canvas já leem.
const isCall = computed(() => isOn('call'));
const whatsappOn = computed(() => isOn('whatsapp'));

const emitChannels = next => {
  const enabled = next.filter(c => c.enabled);
  emit('update', {
    channels: next,
    channel: enabled[0]?.key || 'whatsapp',
  });
};

const setChannelEnabled = (key, on) =>
  emitChannels(
    channels.value.map(c => (c.key === key ? { ...c, enabled: on } : c))
  );

const moveChannel = (key, delta) => {
  const list = [...channels.value];
  const idx = list.findIndex(c => c.key === key);
  const target = idx + delta;
  if (idx === -1 || target < 0 || target >= list.length) return;
  [list[idx], list[target]] = [list[target], list[idx]];
  emitChannels(list);
};

// Contagem de dado por canal nas prospecções ESCOLHIDAS. Lista antiga sem
// contact_counts conta como desconhecida (não bloqueia nada).
const selectedLists = computed(() =>
  savedLists.value.filter(l => isQueued(l.id))
);
const dataCounts = computed(() => {
  const totals = { whatsapp: 0, phone: 0, email: 0, instagram: 0 };
  let known = false;
  selectedLists.value.forEach(l => {
    const counts = l.contact_counts;
    if (!counts || typeof counts !== 'object') return;
    known = true;
    Object.keys(totals).forEach(k => {
      totals[k] += Number(counts[k]) || 0;
    });
  });
  return { known, totals };
});
const dataCount = key => dataCounts.value.totals[CHANNEL_DATA[key]] || 0;
const dataKnown = computed(
  () => selectedLists.value.length > 0 && dataCounts.value.known
);
// Sem o dado, o canal não pode ser ligado; se já estava ligado, mostra o
// aviso em vez de mudar o nó por conta própria.
const isBlocked = key => dataKnown.value && dataCount(key) === 0;
const dataLabel = key =>
  `AI_AGENTS.FLOW.PROSPECT.CHANNELS.${CHANNEL_DATA[key].toUpperCase()}`;

// Contadores por dado na lista de prospecções (📱 telefone · ✉️ e-mail · 📷 IG).
const listCounts = l => {
  const c = l.contact_counts;
  if (!c || typeof c !== 'object') return [];
  return [
    { icon: 'i-lucide-message-circle', value: Number(c.whatsapp) || 0 },
    { icon: 'i-lucide-phone', value: Number(c.phone) || 0 },
    { icon: 'i-lucide-mail', value: Number(c.email) || 0 },
    { icon: 'i-lucide-instagram', value: Number(c.instagram) || 0 },
  ].filter(x => x.value > 0);
};

// SMS: texto curto com tokens (Campaigns::MessageInterpolation no backend).
const SMS_TOKENS = ['primeiro_nome', 'nome', 'empresa', 'cidade'];
const SMS_SOFT_LIMIT = 160;
const smsLength = computed(() => (props.data.smsText || '').length);
const insertSmsToken = token =>
  up('smsText', `${props.data.smsText || ''}{{${token}}}`);

// E-mail: caixa de e-mail do cliente + template de marketing (assunto + HTML).
const emailInboxes = computed(() =>
  (store.getters['inboxes/getInboxes'] || []).filter(
    i => i.channel_type === 'Channel::Email'
  )
);
const emailTemplates = ref([]);
const loadEmailTemplates = async () => {
  try {
    const { data } = await MarketingEmailTemplatesAPI.get();
    emailTemplates.value = Array.isArray(data) ? data : [];
  } catch {
    emailTemplates.value = [];
  }
};
const flexAssistants = ref([]);
const loadAssistants = async () => {
  try {
    const { data } = await flexcallsAPI.assistants();
    flexAssistants.value = Array.isArray(data) ? data : data?.data || [];
  } catch {
    flexAssistants.value = [];
  }
};

// Assistente de voz FlexCalls: cada um pertence a um agente da conta (ou
// nenhum). Um assistente já em uso por OUTRO agente aparece desabilitado —
// vincular o mesmo assistente a dois agentes bagunça pra quem recebe a
// ligação.
const currentAgentId = computed(() => Number(route.params.agentId));
const assistantLabel = a => `${a.name} · ${a.phone}`;
const usedByOther = a => a.agent_id && a.agent_id !== currentAgentId.value;
const selectedAssistant = computed(() =>
  flexAssistants.value.find(
    a => String(a.id) === String(props.data.flexcallsAssistantId || '')
  )
);

// Preview do template escolhido (pra ver o texto que sai no 1º contato).
const selectedTemplate = computed(() =>
  approvedTemplates.value.find(tpl => tpl.name === props.data.templateId)
);
const templatePreview = computed(() => {
  const comps = selectedTemplate.value?.components || [];
  const get = type =>
    comps.find(c => (c.type || '').toUpperCase() === type) || null;
  const header = get('HEADER');
  const buttons = get('BUTTONS');
  return {
    headerImage: (header?.format || '').toUpperCase() === 'IMAGE',
    headerImageUrl: header?.example?.header_handle?.[0] || '',
    headerText:
      (header?.format || '').toUpperCase() === 'TEXT' ? header.text : '',
    body: get('BODY')?.text || '',
    footer: get('FOOTER')?.text || '',
    // O tipo vem junto porque o WhatsApp desenha ícone diferente para resposta
    // rápida, link e telefone — sem ele o preview mente sobre o que o cliente vê.
    buttons: (buttons?.buttons || [])
      .filter(b => b.text)
      .map(b => ({
        text: b.text,
        type: (b.type || 'QUICK_REPLY').toUpperCase(),
      })),
  };
});

// Ícone do botão conforme o WhatsApp desenha: link, telefone ou resposta rápida.
const buttonIcon = type => {
  if (type === 'URL') return 'i-lucide-external-link';
  if (type === 'PHONE_NUMBER') return 'i-lucide-phone';
  return 'i-lucide-corner-up-left';
};

// Tokens oferecidos nas variáveis. Mesmo vocabulário que o backend interpola
// (LeadProspector::ProspectService::VARIABLE_TOKENS) — não invente nomes aqui.
const VARIABLE_TOKENS = [
  'nome',
  'empresa',
  'cidade',
  'bairro',
  'ramo',
  'funcionarios',
  // Assinatura invisível e única: entra só onde o usuário clicar, não em toda
  // variável. Some no preview de propósito — é invisível para o cliente também.
  'codigo',
];

// Lead fictício só do preview: mostra como a mensagem fica preenchida.
const SAMPLE_LEAD = {
  nome: 'João',
  empresa: 'Padaria do Zé',
  cidade: 'Curitiba',
  bairro: 'Batel',
  ramo: 'Padaria',
  funcionarios: '12',
  codigo: '',
};

// Índices {{n}} declarados no corpo do template, em ordem.
const bodyVariableIndexes = computed(() => {
  const matches =
    (templatePreview.value.body || '').match(/\{\{\s*\d+\s*\}\}/g) || [];
  const found = matches.map(m => Number(m.replace(/\D/g, '')));
  return [...new Set(found)].sort((a, b) => a - b);
});

const templateParams = computed(() => props.data.templateParams || {});

const setTemplateParam = (index, value) => {
  emit('update', {
    templateParams: { ...templateParams.value, [index]: value },
  });
};

const insertToken = (index, token) => {
  const current = templateParams.value[index] || '';
  setTemplateParam(index, `${current}{{${token}}}`);
};

// Preview: troca os tokens pelo lead de exemplo e depois {{n}} pelo valor da
// variável. Token desconhecido some, igual ao backend.
const fillTokens = text =>
  (text || '').replace(
    /\{\{\s*([\p{L}_]+)\s*\}\}/gu,
    (_, key) => SAMPLE_LEAD[String(key).toLowerCase()] ?? ''
  );

const previewBody = computed(() =>
  (templatePreview.value.body || '').replace(
    /\{\{\s*(\d+)\s*\}\}/g,
    (_, idx) => {
      const custom = templateParams.value[idx];
      if (custom) return fillTokens(custom);
      return Number(idx) === 1 ? SAMPLE_LEAD.nome : SAMPLE_LEAD.empresa;
    }
  )
);

// Rótulo "{{n}}" ao lado do campo. Função à parte porque o compilador de
// templates do Vue não aceita chaves duplas literais dentro de uma
// interpolação `{{ ... }}` (confunde com o próprio delimitador).
const varLabel = idx => `{{${idx}}}`;

// Assistente de IA: gera a mensagem (1º contato / follow-up) a partir do nicho.
const gen = async (kind, field) => {
  if (!props.data.niche) {
    useAlert(t('AI_AGENTS.FLOW.PROSPECT.NEED_NICHE'));
    return;
  }
  generating.value = field;
  try {
    const { data: r } = await prospectingAPI.generateCopy({
      niche: props.data.niche,
      location: props.data.location,
      kind,
      network: copyNetwork.value,
    });
    if (r.message) up(field, r.message);
  } catch (e) {
    useAlert(t('AI_AGENTS.FLOW.PROSPECT.GEN_ERROR'));
  } finally {
    generating.value = '';
  }
};

onMounted(() => {
  store.dispatch('funnels/get');
  store.dispatch('inboxes/get');
  loadAssistants();
  loadSavedLists();
  loadEmailTemplates();
});
</script>

<template>
  <div class="space-y-3 p-4">
    <div class="mb-1 flex items-center justify-between">
      <div class="flex items-center gap-2">
        <span class="i-lucide-target text-n-slate-11" />
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ data.label || t('AI_AGENTS.FLOW.PROSPECT.TITLE') }}
        </h3>
      </div>
      <button class="i-lucide-x text-n-slate-10" @click="emit('close')" />
    </div>
    <p class="text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.PROSPECT.SUBTITLE') }}
    </p>

    <!-- Modelo: campanha (gatilho) dispara o fluxo; resposta vai pro Agente. -->
    <div
      class="flex items-start gap-2 rounded-lg border border-n-teal-6 bg-n-teal-2 px-3 py-2 text-xs text-n-teal-11"
    >
      <span class="i-lucide-zap mt-0.5 shrink-0" />
      <span>{{ t('AI_AGENTS.FLOW.PROSPECT.TRIGGER_NOTE') }}</span>
    </div>

    <!-- Origem dos leads: fila de prospecções salvas, trabalhadas em ordem -->
    <div class="mb-3">
      <label class="mb-1 block text-xs font-medium text-n-slate-11">
        {{ t('AI_AGENTS.FLOW.PROSPECT.LEAD_SOURCE_LIST_MULTI') }}
      </label>
      <p v-if="!savedLists.length" class="text-xs text-n-slate-10">
        {{ t('AI_AGENTS.FLOW.PROSPECT.LEAD_SOURCE_LIST_EMPTY') }}
      </p>
      <!-- Fechada por padrão quando já há escolha: com muitas prospecções
           salvas a lista inteira toma o painel. -->
      <button
        v-else
        type="button"
        data-testid="prospect-list-toggle"
        class="flex w-full items-center justify-between rounded-lg border border-n-weak px-3 py-2 text-sm text-n-slate-12"
        @click="listOpen = !listOpen"
      >
        <span>
          {{
            queuedListIds.length
              ? t('AI_AGENTS.FLOW.PROSPECT.LEAD_SOURCE_SUMMARY', {
                  count: queuedListIds.length,
                  total: queuedTotal,
                })
              : t('AI_AGENTS.FLOW.PROSPECT.LEAD_SOURCE_SUMMARY_EMPTY')
          }}
        </span>
        <span
          :class="listOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
          class="text-n-slate-10"
        />
      </button>
      <div
        v-if="savedLists.length && listOpen"
        class="mt-1 space-y-1 rounded-lg border border-n-weak p-2"
      >
        <label
          v-for="l in savedLists"
          :key="l.id"
          class="flex cursor-pointer items-center gap-2 text-sm text-n-slate-11"
        >
          <input
            type="checkbox"
            :data-testid="`prospect-list-${l.id}`"
            :checked="isQueued(l.id)"
            @change="toggleList(l.id, $event.target.checked)"
          />
          <span
            v-if="queuePosition(l.id)"
            :data-testid="`prospect-list-order-${l.id}`"
            class="rounded bg-n-alpha-2 px-1.5 text-[11px] text-n-slate-11"
          >
            {{ queuePosition(l.id) }}
          </span>
          <span
            v-if="l.mode === 'crm'"
            class="i-lucide-refresh-cw shrink-0 text-n-teal-10"
            :title="t('AI_AGENTS.FLOW.PROSPECT.CRM_LIVE')"
          />
          <span class="text-n-slate-12">{{ l.name }}</span>
          <span class="text-xs text-n-slate-10">
            · {{ l.total_count || l.estimated_count || 0 }}
          </span>
          <span
            v-if="listCounts(l).length"
            :data-testid="`prospect-list-counts-${l.id}`"
            class="ml-auto flex shrink-0 items-center gap-1.5 text-[11px] text-n-slate-10"
          >
            <span
              v-for="c in listCounts(l)"
              :key="c.icon"
              class="flex items-center gap-0.5"
            >
              <span :class="c.icon" />
              {{ c.value }}
            </span>
          </span>
        </label>
      </div>
      <button
        v-if="!crmFormOpen"
        type="button"
        data-testid="prospect-crm-open"
        class="mt-2 flex items-center gap-1.5 text-xs font-medium text-n-brand hover:underline"
        @click="crmFormOpen = true"
      >
        <span class="i-lucide-plus" />
        {{ t('AI_AGENTS.FLOW.PROSPECT.FROM_CRM') }}
      </button>
      <div
        v-else
        class="mt-2 rounded-lg border border-n-weak p-3"
        data-testid="prospect-crm-form"
      >
        <CrmSourceForm
          compact
          @created="onCrmListCreated"
          @cancel="crmFormOpen = false"
        />
      </div>
      <p class="mt-1 text-xs text-n-slate-10">
        {{ t('AI_AGENTS.FLOW.PROSPECT.LEAD_SOURCE_LIST_HINT') }}
      </p>
    </div>

    <!-- Canais do 1º contato: ordem de tentativa + cota por dia. Um canal só
         liga se as prospecções escolhidas têm o dado dele. -->
    <div>
      <label class="mb-1 block text-xs font-medium text-n-slate-11">
        {{ t('AI_AGENTS.FLOW.PROSPECT.CHANNELS.TITLE') }}
      </label>
      <div
        class="divide-y divide-n-weak rounded-lg border border-n-weak bg-n-solid-2"
      >
        <div
          v-for="(c, idx) in channels"
          :key="c.key"
          :data-testid="`prospect-channel-${c.key}`"
          class="flex items-center gap-2 px-2 py-2"
          :class="c.enabled ? '' : 'opacity-80'"
        >
          <span
            class="w-4 shrink-0 text-center text-[11px] tabular-nums text-n-slate-10"
          >
            {{ idx + 1 }}
          </span>
          <input
            type="checkbox"
            :data-testid="`prospect-channel-toggle-${c.key}`"
            :checked="c.enabled"
            :disabled="!c.enabled && isBlocked(c.key)"
            :aria-label="
              t(`AI_AGENTS.FLOW.PROSPECT.CHANNELS.LABEL_${c.key.toUpperCase()}`)
            "
            class="shrink-0 disabled:cursor-not-allowed disabled:opacity-50"
            @change="setChannelEnabled(c.key, $event.target.checked)"
          />
          <span
            :class="[
              CHANNEL_ICONS[c.key],
              c.enabled ? 'text-n-brand' : 'text-n-slate-10',
            ]"
            class="shrink-0"
          />
          <div class="min-w-0 flex-1">
            <p
              class="m-0 text-sm leading-5"
              :class="c.enabled ? 'text-n-slate-12' : 'text-n-slate-11'"
            >
              {{
                t(
                  `AI_AGENTS.FLOW.PROSPECT.CHANNELS.LABEL_${c.key.toUpperCase()}`
                )
              }}
            </p>
            <p
              v-if="isBlocked(c.key)"
              :data-testid="`prospect-channel-blocked-${c.key}`"
              class="m-0 flex items-start gap-1 text-[11px] leading-4 text-n-amber-11"
            >
              <span class="i-lucide-alert-triangle mt-0.5 shrink-0" />
              <span>
                {{
                  t('AI_AGENTS.FLOW.PROSPECT.CHANNELS.MISSING', {
                    data: t(dataLabel(c.key)),
                  })
                }}
                {{
                  c.key === 'instagram'
                    ? t(
                        'AI_AGENTS.FLOW.PROSPECT.CHANNELS.MISSING_INSTAGRAM_HINT'
                      )
                    : ''
                }}
              </span>
            </p>
            <p
              v-else-if="dataKnown"
              :data-testid="`prospect-channel-count-${c.key}`"
              class="m-0 text-[11px] leading-4 text-n-slate-10"
            >
              {{
                t('AI_AGENTS.FLOW.PROSPECT.CHANNELS.COUNT', {
                  count: dataCount(c.key),
                  data: t(dataLabel(c.key)),
                })
              }}
            </p>
          </div>
          <span
            :data-testid="`prospect-channel-cost-${c.key}`"
            class="shrink-0 rounded-full px-2 py-0.5 text-[11px] tabular-nums"
            :class="
              c.enabled
                ? 'bg-n-alpha-2 text-n-slate-11'
                : 'bg-transparent text-n-slate-9'
            "
          >
            {{
              t(`AI_AGENTS.FLOW.PROSPECT.CHANNELS.COST_${c.key.toUpperCase()}`)
            }}
          </span>
          <div class="flex shrink-0 flex-col">
            <button
              type="button"
              :data-testid="`prospect-channel-up-${c.key}`"
              class="i-lucide-chevron-up text-n-slate-10 hover:text-n-slate-12 disabled:opacity-30"
              :disabled="idx === 0"
              :aria-label="t('AI_AGENTS.FLOW.PROSPECT.CHANNELS.MOVE_UP')"
              @click="moveChannel(c.key, -1)"
            />
            <button
              type="button"
              :data-testid="`prospect-channel-down-${c.key}`"
              class="i-lucide-chevron-down text-n-slate-10 hover:text-n-slate-12 disabled:opacity-30"
              :disabled="idx === channels.length - 1"
              :aria-label="t('AI_AGENTS.FLOW.PROSPECT.CHANNELS.MOVE_DOWN')"
              @click="moveChannel(c.key, 1)"
            />
          </div>
        </div>
      </div>
      <p class="mt-1 text-xs text-n-slate-10">
        {{ t('AI_AGENTS.FLOW.PROSPECT.CHANNELS.HINT') }}
      </p>
      <p
        v-if="!dataKnown && queuedListIds.length"
        class="mt-1 text-xs text-n-slate-10"
      >
        {{ t('AI_AGENTS.FLOW.PROSPECT.CHANNELS.UNKNOWN') }}
      </p>
      <p
        v-if="!enabledChannels.length"
        data-testid="prospect-channels-none"
        class="mt-1 flex items-start gap-1.5 text-xs text-n-amber-11"
      >
        <span class="i-lucide-alert-triangle mt-0.5 shrink-0" />
        {{ t('AI_AGENTS.FLOW.PROSPECT.CHANNELS.NONE') }}
      </p>
    </div>

    <!-- Ligação: escolher o assistente FlexCalls vinculado a este agente -->
    <div
      v-if="isCall"
      data-testid="prospect-config-call"
      class="flex flex-col gap-3 rounded-lg border border-n-weak p-3"
    >
      <div class="flex items-center gap-2 text-xs font-medium text-n-slate-12">
        <span :class="CHANNEL_ICONS.call" class="text-n-brand" />
        {{ t('AI_AGENTS.FLOW.PROSPECT.CHANNELS.LABEL_CALL') }}
      </div>
      <div>
        <label class="mb-1 block text-xs font-medium text-n-slate-11">
          {{ t('AI_AGENTS.FLOW.PROSPECT.ASSISTANT') }}
        </label>
        <select
          data-testid="prospect-assistant"
          :value="data.flexcallsAssistantId || ''"
          :class="selectClass"
          @change="up('flexcallsAssistantId', $event.target.value || null)"
        >
          <option value="">
            {{ t('AI_AGENTS.FLOW.PROSPECT.ASSISTANT_PICK') }}
          </option>
          <option
            v-for="a in flexAssistants"
            :key="a.id"
            :value="String(a.id)"
            :disabled="usedByOther(a)"
          >
            {{ assistantLabel(a)
            }}{{
              usedByOther(a)
                ? ` — ${t('AI_AGENTS.FLOW.PROSPECT.ASSISTANT_IN_USE', {
                    agent: a.agent_name,
                  })}`
                : ''
            }}
          </option>
        </select>
        <p
          v-if="!flexAssistants.length"
          class="mt-1 flex items-start gap-1.5 text-xs text-n-amber-11"
        >
          <span class="i-lucide-alert-triangle mt-0.5 shrink-0" />
          {{ t('AI_AGENTS.FLOW.PROSPECT.ASSISTANT_NONE') }}
        </p>
      </div>

      <div
        v-if="selectedAssistant"
        data-testid="prospect-assistant-card"
        class="rounded-lg border border-n-weak bg-n-solid-2 p-3 text-xs text-n-slate-11"
      >
        <div class="flex items-center justify-between gap-2">
          <span class="font-medium text-n-slate-12">{{
            selectedAssistant.name
          }}</span>
          <a
            data-testid="prospect-assistant-test"
            :href="selectedAssistant.widget_url"
            target="_blank"
            rel="noopener noreferrer"
            class="flex items-center gap-1 text-n-brand hover:underline"
          >
            <span class="i-lucide-play" />
            {{ t('AI_AGENTS.FLOW.PROSPECT.ASSISTANT_TEST') }}
          </a>
        </div>
        <p class="mt-1">
          {{
            t('AI_AGENTS.FLOW.PROSPECT.ASSISTANT_PHONE', {
              phone: selectedAssistant.phone,
            })
          }}
        </p>
        <p>{{ t('AI_AGENTS.FLOW.PROSPECT.ASSISTANT_PROMPT_HINT') }}</p>
        <p
          v-if="!selectedAssistant.webhook_ok || selectedAssistant.last_error"
          class="mt-1 flex items-start gap-1 text-n-amber-11"
        >
          <span class="i-lucide-alert-triangle mt-0.5 shrink-0" />
          {{
            selectedAssistant.last_error ||
            t('AI_AGENTS.FLOW.PROSPECT.ASSISTANT_WEBHOOK_OFF')
          }}
        </p>
      </div>

      <div>
        <label class="mb-1 block text-xs font-medium text-n-slate-11">
          {{ t('AI_AGENTS.FLOW.PROSPECT.CALL_GREETING') }}
        </label>
        <input
          data-testid="prospect-call-greeting"
          type="text"
          maxlength="200"
          :value="data.callGreeting || ''"
          :placeholder="t('AI_AGENTS.FLOW.PROSPECT.CALL_GREETING_PLACEHOLDER')"
          :class="selectClass"
          @input="up('callGreeting', $event.target.value)"
        />
      </div>
      <p class="text-xs text-n-teal-11">
        {{ t('AI_AGENTS.FLOW.PROSPECT.CALL_HINT') }}
      </p>
      <p
        v-if="!(data.prospectingListIds || []).length"
        data-testid="prospect-call-discovery-hint"
        class="text-xs text-n-amber-11"
      >
        {{ t('AI_AGENTS.FLOW.PROSPECT.CALL_DISCOVERY_HINT') }}
      </p>
    </div>

    <!-- WhatsApp: número de envio (oficial Meta = anti-ban) + template -->
    <div
      v-if="whatsappOn"
      data-testid="prospect-config-whatsapp"
      class="flex flex-col gap-3 rounded-lg border border-n-weak p-3"
    >
      <div class="flex items-center gap-2 text-xs font-medium text-n-slate-12">
        <span :class="CHANNEL_ICONS.whatsapp" class="text-n-brand" />
        {{ t('AI_AGENTS.FLOW.PROSPECT.CHANNELS.LABEL_WHATSAPP') }}
      </div>
      <div>
        <label class="mb-1 block text-xs font-medium text-n-slate-11">
          {{ t('AI_AGENTS.FLOW.PROSPECT.NUMBER') }}
        </label>
        <select
          :value="data.whatsappInboxId || ''"
          :class="selectClass"
          @change="onInboxChange"
        >
          <option value="">
            {{ t('AI_AGENTS.FLOW.PROSPECT.NUMBER_DEFAULT') }}
          </option>
          <option v-for="i in whatsappInboxes" :key="i.id" :value="i.id">
            {{ i.name }}
            {{
              i.official
                ? t('AI_AGENTS.FLOW.PROSPECT.OFFICIAL_TAG')
                : t('AI_AGENTS.FLOW.PROSPECT.UNOFFICIAL_TAG')
            }}
          </option>
        </select>
      </div>

      <!-- Oficial Meta: template aprovado (HSM) -->
      <div v-if="isOfficialMeta">
        <label class="mb-1 block text-xs font-medium text-n-slate-11">
          {{ t('AI_AGENTS.FLOW.PROSPECT.TEMPLATE') }}
        </label>
        <select
          :value="data.templateId || ''"
          :class="selectClass"
          @change="up('templateId', $event.target.value || null)"
        >
          <option value="">
            {{ t('AI_AGENTS.FLOW.PROSPECT.TEMPLATE_PICK') }}
          </option>
          <option
            v-if="isUnknownTemplate(data.templateId)"
            :value="data.templateId"
          >
            {{
              t('AI_AGENTS.FLOW.PROSPECT.TEMPLATE_UNAVAILABLE', {
                name: data.templateId,
              })
            }}
          </option>
          <option
            v-for="tpl in approvedTemplates"
            :key="tpl.id"
            :value="tpl.name"
          >
            {{ tpl.name }}
          </option>
        </select>
        <p
          v-if="!approvedTemplates.length"
          class="mt-1 flex items-start gap-1.5 text-xs text-n-amber-11"
        >
          <span class="i-lucide-alert-triangle mt-0.5 shrink-0" />
          {{ t('AI_AGENTS.FLOW.PROSPECT.NO_TEMPLATES') }}
        </p>
        <p v-else class="mt-1 text-xs text-n-teal-11">
          {{ t('AI_AGENTS.FLOW.PROSPECT.TEMPLATE_HINT') }}
        </p>

        <!-- Preview do template selecionado (como o cliente recebe) -->
        <div
          v-if="selectedTemplate"
          class="mt-2 rounded-lg border border-n-weak bg-n-alpha-1 p-3"
        >
          <div
            class="mb-1.5 flex items-center gap-1 text-[11px] text-n-slate-10"
          >
            <span class="i-lucide-eye" />
            {{ t('AI_AGENTS.FLOW.PROSPECT.PREVIEW') }}
          </div>
          <div
            class="rounded-lg bg-[#dcf8c6] p-2.5 text-xs text-n-slate-12 dark:bg-n-teal-3"
          >
            <img
              v-if="
                templatePreview.headerImage && templatePreview.headerImageUrl
              "
              :src="templatePreview.headerImageUrl"
              data-testid="tpl-preview-image"
              class="mb-1.5 max-h-32 w-full rounded object-cover"
              alt=""
            />
            <div
              v-else-if="templatePreview.headerImage"
              class="mb-1.5 flex h-16 items-center justify-center rounded bg-n-alpha-2 text-n-slate-10"
            >
              <span class="i-lucide-image text-lg" />
            </div>
            <p
              v-if="templatePreview.headerText"
              class="mb-1 font-semibold text-n-slate-12"
            >
              {{ templatePreview.headerText }}
            </p>
            <p data-testid="tpl-preview-body" class="whitespace-pre-line">
              {{ previewBody }}
            </p>
            <p v-if="templatePreview.footer" class="mt-1 text-n-slate-10">
              {{ templatePreview.footer }}
            </p>
            <!-- Botões como o WhatsApp desenha de verdade: dentro do balão,
               largura inteira, empilhados e separados por divisória — não
               chips soltos embaixo. -->
            <div v-if="templatePreview.buttons.length" class="-mx-2.5 mt-2">
              <div
                v-for="(b, idx) in templatePreview.buttons"
                :key="idx"
                :data-testid="`tpl-preview-button-${idx}`"
                class="flex items-center justify-center gap-1.5 border-t border-n-slate-6 px-2 py-1.5 text-[11px] font-medium text-[#0091ea] dark:border-n-weak dark:text-n-blue-11"
              >
                <span :class="buttonIcon(b.type)" class="text-xs" />
                {{ b.text }}
              </div>
            </div>
          </div>
        </div>
        <!-- Variáveis: DEPOIS do preview. Antes elas ficavam no meio e empurravam
           a pré-visualização para longe do template escolhido. -->
        <div v-if="selectedTemplate && bodyVariableIndexes.length" class="mt-3">
          <label class="mb-1 block text-xs font-medium text-n-slate-11">
            {{ t('AI_AGENTS.FLOW.PROSPECT.VARIABLES') }}
          </label>
          <div v-for="idx in bodyVariableIndexes" :key="idx" class="mb-2">
            <div class="mb-1 flex items-center gap-2">
              <span class="text-xs text-n-slate-10">{{ varLabel(idx) }}</span>
              <input
                :data-testid="`tpl-var-${idx}`"
                :value="templateParams[idx] || ''"
                class="flex-1 rounded-lg border border-n-weak bg-n-solid-2 px-2 py-1.5 text-sm text-n-slate-12"
                @input="setTemplateParam(idx, $event.target.value)"
              />
            </div>
            <div class="flex flex-wrap gap-1">
              <button
                v-for="tk in VARIABLE_TOKENS"
                :key="tk"
                type="button"
                :data-testid="`tpl-token-${idx}-${tk}`"
                class="rounded border border-n-weak px-1.5 py-0.5 text-[10px] text-n-slate-11 hover:bg-n-alpha-2"
                @click="insertToken(idx, tk)"
              >
                {{ t(`AI_AGENTS.FLOW.PROSPECT.TOKEN_${tk.toUpperCase()}`) }}
              </button>
            </div>
          </div>
          <p class="text-xs text-n-slate-10">
            {{ t('AI_AGENTS.FLOW.PROSPECT.VARIABLES_HINT') }}
          </p>
        </div>
      </div>

      <!-- Não-oficial: aviso de risco de ban -->
      <div
        v-else-if="data.whatsappInboxId"
        class="flex items-start gap-2 rounded-lg border border-n-ruby-6 bg-n-ruby-2 px-3 py-2 text-xs text-n-ruby-11"
      >
        <span class="i-lucide-shield-alert mt-0.5 shrink-0" />
        <span>{{ t('AI_AGENTS.FLOW.PROSPECT.BAN_WARNING') }}</span>
      </div>
      <!-- 1º contato em texto livre: só pra número NÃO-oficial. No oficial Meta o
         1º contato é o template aprovado (HSM); na ligação não tem texto. -->
      <div v-if="!isOfficialMeta">
        <!-- Tom da mensagem gerada por IA: whatsapp / instagram / facebook -->
        <div class="mb-2">
          <label class="mb-1 block text-xs font-medium text-n-slate-11">
            {{ t('AI_AGENTS.FLOW.PROSPECT.COPY_NETWORK') }}
          </label>
          <div class="grid grid-cols-3 gap-2">
            <button
              v-for="n in COPY_NETWORKS"
              :key="n"
              type="button"
              class="flex items-center justify-center gap-1.5 rounded-lg border px-2 py-2 text-xs"
              :class="
                copyNetwork === n
                  ? 'border-n-brand bg-n-brand/10 text-n-slate-12'
                  : 'border-n-weak text-n-slate-11'
              "
              @click="copyNetwork = n"
            >
              <span :class="COPY_NETWORK_ICONS[n]" />
              {{ t(`AI_AGENTS.FLOW.PROSPECT.COPY_NETWORK_${n.toUpperCase()}`) }}
            </button>
          </div>
        </div>
        <div class="mb-1 flex items-center justify-between">
          <label class="text-xs font-medium text-n-slate-11">
            {{ t('AI_AGENTS.FLOW.PROSPECT.MESSAGE') }}
          </label>
          <button
            type="button"
            class="flex items-center gap-1 rounded-md border border-n-weak px-1.5 py-0.5 text-[10px] text-n-brand hover:bg-n-brand/10"
            :disabled="generating === 'messageContent'"
            @click="gen('first_contact', 'messageContent')"
          >
            <span class="i-lucide-sparkles" />
            {{
              generating === 'messageContent'
                ? t('AI_AGENTS.FLOW.PROSPECT.GENERATING')
                : t('AI_AGENTS.FLOW.PROSPECT.GENERATE')
            }}
          </button>
        </div>
        <textarea
          :value="data.messageContent"
          :placeholder="t('AI_AGENTS.FLOW.PROSPECT.MESSAGE_PH')"
          rows="4"
          class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12"
          @input="up('messageContent', $event.target.value)"
        />
      </div>
    </div>

    <!-- SMS: texto curto com tokens; R$0,12 dos créditos, pelo número do ChatSuite -->
    <div
      v-if="isOn('sms')"
      data-testid="prospect-config-sms"
      class="flex flex-col gap-2 rounded-lg border border-n-weak p-3"
    >
      <div class="flex items-center gap-2 text-xs font-medium text-n-slate-12">
        <span :class="CHANNEL_ICONS.sms" class="text-n-brand" />
        {{ t('AI_AGENTS.FLOW.PROSPECT.CHANNELS.LABEL_SMS') }}
      </div>
      <div class="flex items-center justify-between">
        <label class="text-xs font-medium text-n-slate-11">
          {{ t('AI_AGENTS.FLOW.PROSPECT.CHANNELS.SMS_TEXT') }}
        </label>
        <span
          data-testid="prospect-sms-counter"
          class="text-[11px] tabular-nums"
          :class="
            smsLength > SMS_SOFT_LIMIT ? 'text-n-amber-11' : 'text-n-slate-10'
          "
        >
          {{
            t('AI_AGENTS.FLOW.PROSPECT.CHANNELS.SMS_COUNTER', {
              count: smsLength,
              max: SMS_SOFT_LIMIT,
            })
          }}
        </span>
      </div>
      <textarea
        data-testid="prospect-sms-text"
        :value="data.smsText || ''"
        :placeholder="t('AI_AGENTS.FLOW.PROSPECT.CHANNELS.SMS_PH')"
        rows="3"
        class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12"
        @input="up('smsText', $event.target.value)"
      />
      <div class="flex flex-wrap gap-1">
        <button
          v-for="tk in SMS_TOKENS"
          :key="tk"
          type="button"
          :data-testid="`prospect-sms-token-${tk}`"
          class="rounded border border-n-weak px-1.5 py-0.5 text-[10px] text-n-slate-11 hover:bg-n-alpha-2"
          @click="insertSmsToken(tk)"
        >
          {{ t(`AI_AGENTS.FLOW.PROSPECT.TOKEN_${tk.toUpperCase()}`) }}
        </button>
      </div>
      <p class="text-xs text-n-teal-11">
        {{ t('AI_AGENTS.FLOW.PROSPECT.CHANNELS.SMS_NOTE') }}
      </p>
    </div>

    <!-- E-mail: caixa do cliente + template de marketing. Grátis. -->
    <div
      v-if="isOn('email')"
      data-testid="prospect-config-email"
      class="flex flex-col gap-2 rounded-lg border border-n-weak p-3"
    >
      <div class="flex items-center gap-2 text-xs font-medium text-n-slate-12">
        <span :class="CHANNEL_ICONS.email" class="text-n-brand" />
        {{ t('AI_AGENTS.FLOW.PROSPECT.CHANNELS.LABEL_EMAIL') }}
      </div>
      <div>
        <label class="mb-1 block text-xs font-medium text-n-slate-11">
          {{ t('AI_AGENTS.FLOW.PROSPECT.CHANNELS.EMAIL_INBOX') }}
        </label>
        <select
          data-testid="prospect-email-inbox"
          :value="data.emailInboxId || ''"
          :class="selectClass"
          @change="up('emailInboxId', Number($event.target.value) || null)"
        >
          <option value="">
            {{ t('AI_AGENTS.FLOW.PROSPECT.CHANNELS.EMAIL_INBOX_PICK') }}
          </option>
          <option v-for="i in emailInboxes" :key="i.id" :value="i.id">
            {{ i.name }}
          </option>
        </select>
        <p
          v-if="!emailInboxes.length"
          class="mt-1 flex items-start gap-1.5 text-xs text-n-amber-11"
        >
          <span class="i-lucide-alert-triangle mt-0.5 shrink-0" />
          {{ t('AI_AGENTS.FLOW.PROSPECT.CHANNELS.EMAIL_INBOX_NONE') }}
        </p>
      </div>
      <div>
        <label class="mb-1 block text-xs font-medium text-n-slate-11">
          {{ t('AI_AGENTS.FLOW.PROSPECT.CHANNELS.EMAIL_TEMPLATE') }}
        </label>
        <select
          data-testid="prospect-email-template"
          :value="data.emailTemplateId || ''"
          :class="selectClass"
          @change="up('emailTemplateId', Number($event.target.value) || null)"
        >
          <option value="">
            {{ t('AI_AGENTS.FLOW.PROSPECT.CHANNELS.EMAIL_TEMPLATE_PICK') }}
          </option>
          <option v-for="tpl in emailTemplates" :key="tpl.id" :value="tpl.id">
            {{ tpl.name }}{{ tpl.subject ? ` — ${tpl.subject}` : '' }}
          </option>
        </select>
        <p
          v-if="!emailTemplates.length"
          class="mt-1 flex items-start gap-1.5 text-xs text-n-amber-11"
        >
          <span class="i-lucide-alert-triangle mt-0.5 shrink-0" />
          {{ t('AI_AGENTS.FLOW.PROSPECT.CHANNELS.EMAIL_TEMPLATE_NONE') }}
        </p>
      </div>
      <p class="text-xs text-n-teal-11">
        {{ t('AI_AGENTS.FLOW.PROSPECT.CHANNELS.EMAIL_NOTE') }}
      </p>
    </div>

    <!-- Instagram assistido: a IA prepara a DM por perfil; o envio é manual -->
    <div
      v-if="isOn('instagram')"
      data-testid="prospect-config-instagram"
      class="flex flex-col gap-2 rounded-lg border border-n-weak p-3"
    >
      <div class="flex items-center gap-2 text-xs font-medium text-n-slate-12">
        <span :class="CHANNEL_ICONS.instagram" class="text-n-brand" />
        {{ t('AI_AGENTS.FLOW.PROSPECT.CHANNELS.LABEL_INSTAGRAM') }}
      </div>
      <label class="text-xs font-medium text-n-slate-11">
        {{ t('AI_AGENTS.FLOW.PROSPECT.CHANNELS.INSTAGRAM_TEXT') }}
      </label>
      <textarea
        data-testid="prospect-instagram-text"
        :value="data.instagramMessage || ''"
        :placeholder="t('AI_AGENTS.FLOW.PROSPECT.CHANNELS.INSTAGRAM_PH')"
        rows="3"
        class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12"
        @input="up('instagramMessage', $event.target.value)"
      />
      <p class="text-xs text-n-teal-11">
        {{ t('AI_AGENTS.FLOW.PROSPECT.CHANNELS.INSTAGRAM_NOTE') }}
      </p>
    </div>

    <div>
      <label class="mb-1 block text-xs font-medium text-n-slate-11">
        {{ t('AI_AGENTS.FLOW.PROSPECT.FUNNEL') }}
      </label>
      <select
        :value="data.funnelId || ''"
        data-testid="prospect-funnel"
        :class="selectClass"
        @change="onFunnelChange"
      >
        <option value="">
          {{ t('AI_AGENTS.FLOW.PROSPECT.FUNNEL_DEFAULT') }}
        </option>
        <option v-for="f in funnels" :key="f.id" :value="f.id">
          {{ f.name }}
        </option>
      </select>
    </div>

    <div v-if="data.funnelId">
      <label class="mb-1 block text-xs font-medium text-n-slate-11">
        {{ t('AI_AGENTS.FLOW.PROSPECT.STAGE') }}
      </label>
      <select
        :value="data.stageName || ''"
        data-testid="prospect-stage"
        :class="selectClass"
        @change="up('stageName', $event.target.value)"
      >
        <option value="">
          {{ t('AI_AGENTS.FLOW.PROSPECT.STAGE_DEFAULT') }}
        </option>
        <option
          v-for="st in stagesOf(data.funnelId)"
          :key="st.name"
          :value="st.name"
        >
          {{ st.label }}
        </option>
      </select>
    </div>

    <div class="grid grid-cols-3 gap-2">
      <div>
        <label class="mb-1 block text-xs font-medium text-n-slate-11">
          {{ t('AI_AGENTS.FLOW.PROSPECT.DAILY') }}
        </label>
        <input
          data-testid="prospect-daily-limit"
          :value="data.dailyLimit ?? 30"
          type="number"
          min="1"
          max="1000"
          class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-2 py-2 text-sm text-n-slate-12"
          @input="upNum('dailyLimit', $event.target.value)"
        />
      </div>
      <div>
        <label class="mb-1 block text-xs font-medium text-n-slate-11">
          {{ t('AI_AGENTS.FLOW.PROSPECT.START_HOUR') }}
        </label>
        <input
          :value="data.startHour ?? 8"
          type="number"
          min="0"
          max="23"
          class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-2 py-2 text-sm text-n-slate-12"
          @input="upNum('startHour', $event.target.value)"
        />
      </div>
      <div>
        <label class="mb-1 block text-xs font-medium text-n-slate-11">
          {{ t('AI_AGENTS.FLOW.PROSPECT.END_HOUR') }}
        </label>
        <input
          :value="data.endHour ?? 20"
          type="number"
          min="1"
          max="24"
          class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-2 py-2 text-sm text-n-slate-12"
          @input="upNum('endHour', $event.target.value)"
        />
      </div>
    </div>

    <div>
      <label class="mb-1 block text-xs font-medium text-n-slate-11">
        {{ t('AI_AGENTS.FLOW.PROSPECT.TOTAL_TARGET') }}
      </label>
      <input
        :value="data.totalTarget || ''"
        type="number"
        min="1"
        :placeholder="t('AI_AGENTS.FLOW.PROSPECT.TOTAL_TARGET_PH')"
        class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12"
        @input="up('totalTarget', Number($event.target.value) || null)"
      />
      <p class="mt-1 text-xs text-n-slate-10">
        {{ t('AI_AGENTS.FLOW.PROSPECT.TOTAL_TARGET_HINT') }}
      </p>
    </div>

    <label
      class="flex cursor-pointer items-center gap-2 text-sm text-n-slate-11"
    >
      <input
        type="checkbox"
        :checked="data.weekdaysOnly === true"
        @change="up('weekdaysOnly', $event.target.checked)"
      />
      <span>{{ t('AI_AGENTS.FLOW.PROSPECT.WEEKDAYS_ONLY') }}</span>
    </label>

    <label
      class="flex cursor-pointer items-center gap-2 text-sm text-n-slate-11"
    >
      <input
        type="checkbox"
        :checked="data.enabled === true"
        @change="up('enabled', $event.target.checked)"
      />
      <span>{{ t('AI_AGENTS.FLOW.PROSPECT.ENABLED') }}</span>
    </label>

    <p class="text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.PROSPECT.HINT') }}
    </p>

    <button
      class="mt-2 w-full rounded-lg border border-n-ruby-7 py-2 text-sm font-medium text-n-ruby-9 hover:bg-n-ruby-3"
      @click="emit('remove')"
    >
      {{ t('AI_AGENTS.FLOW.REMOVE_NODE') }}
    </button>
  </div>
</template>
